import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ThreeColumnTemplateComponent } from '@templates/three-column-template/three-column-template.component';
import { ContentService } from '../../services/content.service';
import {
  IGetTopicsItemsResponse,
  IMyTopicsTypes,
  MY_TOPIC_TYPES,
  TopicTypes,
  TypesAPIMapObj,
} from './my-topics.model';
import { _CLEAR, HelperService } from '../../services/helper.service';
import { BehaviorSubject, filter, of, switchMap, take, tap } from 'rxjs';
import { NewsSectionComponent } from '../home/components/news-section/news-section.component';
import {
  PublicationCardComponent,
} from '../home/components/latest-publications-section/components/publication-card/publication-card.component';
import { SimpleCardComponent } from '@shared/components/collections/components/simple-card/simple-card.component';
import { E_COLLECTION_FOLDER_TYPES } from '@shared/components/collections/collection.model';
import { PaginatorComponent } from '@shared/components/paginator/paginator.component';
import { IPaginationProps } from '@shared/constants/models.const';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MarkAllDialogComponent } from './mark-all-dialog/mark-all-dialog.component';

@Component({
  selector: 'app-my-topics',
  imports: [
    ThreeColumnTemplateComponent,
    NewsSectionComponent,
    PublicationCardComponent,
    SimpleCardComponent,
    PaginatorComponent,
    RouterLink,
  ],
  templateUrl: './my-topics.component.html',
  styleUrl: './my-topics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyTopicsComponent implements OnInit {
  isLoading = signal(true);
  sideMenuData = signal(MY_TOPIC_TYPES);
  topicsData = signal<IGetTopicsItemsResponse | null>(null);
  topics = computed(() => this.topicsData()?.items || []);
  hasUnread = computed(() => this.topics().some(item => !item.isRead));

  $selectedType = new BehaviorSubject<TopicTypes>('All');

  take = signal(10);
  skip = signal(0);
  totalRecords = computed(() => this.topicsData()?.totalCount || 0);

  readonly CONTENT = inject(ContentService);
  readonly DIALOG = inject(MatDialog);
  HELPER = inject(HelperService);

  ngOnInit() {

    this.CONTENT.getMySettings()
      .pipe(
        switchMap(data => {
          if (!data.folderSettings.length && !data.topicSettings.length) {
            return this.CONTENT.saveMySettings();
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.sideMenuHandler();
        this.getTopicsHandler();
      });


  }


  paginationChange(props: IPaginationProps) {
    this.take.set(props.take);
    this.skip.set(props.skip);
    this.$selectedType.next(this.$selectedType.value);
  }


  getTopicsHandler() {
    this.$selectedType
      .pipe(
        tap(() => {this.isLoading.set(true);}),
        switchMap((type) => this.CONTENT
          .getTopicsItems(TypesAPIMapObj[type], this.skip(), this.take())),
      )
      .subscribe(data => {
        this.isLoading.set(false);
        if (data) this.topicsData.set(data);
      });
  }


  sideMenuHandler() {
    this.CONTENT.getTopicsContentTypes()
      .subscribe(data => {
        if (!data || !data.length) return;
        const tmpData = _CLEAR(MY_TOPIC_TYPES);
        data.forEach(item => {
          tmpData.forEach((typeItem: IMyTopicsTypes) => {
            if (item.type === typeItem.typeName) {
              typeItem.totalCount = item.totalCount;
              typeItem.unreadCount = item.unreadCount;
            }
          });
        });
        this.sideMenuData.set(_CLEAR(tmpData));
      });
  }


  setSelectedType(item: IMyTopicsTypes) {
    if (item.typeName !== this.$selectedType.value) {
      this.$selectedType.next(item.typeName as TopicTypes);
    }

  }

  protected readonly TYPES = E_COLLECTION_FOLDER_TYPES;

  markAllRead() {

    this.DIALOG.open(MarkAllDialogComponent, { panelClass: 'custom-dialog' })
      .afterClosed()
      .pipe(
        filter(markAll => markAll),
        switchMap(() => this.CONTENT.markAllTopicsRead(TypesAPIMapObj[this.$selectedType.value])),
        take(1),
      )
      .subscribe(data => {
        this.$selectedType.next(this.$selectedType.value);
        this.sideMenuHandler();
      });
    
  }
}
